import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TechTimeClock() {
  const [status, setStatus] = useState('not_clocked_in');
  const [currentEntry, setCurrentEntry] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/timeclock/status`),
        axios.get(`${API}/timelogs`)
      ]);
      setStatus(statusRes.data.status);
      setCurrentEntry(statusRes.data.entry);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error('Error al cargar estado de fichaje');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/timeclock`, { action: 'clock_in' });
      toast.success('Entrada registrada correctamente');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al fichar entrada');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/timeclock`, { action: 'clock_out' });
      toast.success('Salida registrada correctamente');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al fichar salida');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const getElapsedTime = () => {
    if (!currentEntry?.clock_in) return null;
    const start = new Date(currentEntry.clock_in);
    const diff = Math.floor((currentTime - start) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Layout title="Control Horario">
      <div className="space-y-6 animate-fade-in" data-testid="tech-timeclock">
        {/* Current Status Card */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="font-rajdhani text-xl text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-sky-400" />
              Estado Actual
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-500">Cargando...</div>
            ) : (
              <div className="text-center space-y-6">
                {/* Digital Clock */}
                <div className="text-6xl font-mono font-bold text-white">
                  {currentTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
                <p className="text-slate-400 capitalize">
                  {currentTime.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>

                {/* Status Display */}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                  status === 'clocked_in' 
                    ? 'bg-emerald-500/10 text-emerald-400' 
                    : status === 'clocked_out'
                      ? 'bg-slate-500/10 text-slate-400'
                      : 'bg-amber-500/10 text-amber-400'
                }`}>
                  {status === 'clocked_in' ? (
                    <><div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Trabajando</>
                  ) : status === 'clocked_out' ? (
                    <><div className="w-2 h-2 rounded-full bg-slate-400" /> Jornada Completada</>
                  ) : (
                    <><div className="w-2 h-2 rounded-full bg-amber-400" /> Sin Fichar</>
                  )}
                </div>

                {/* Elapsed Time */}
                {status === 'clocked_in' && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500">Tiempo trabajado hoy</p>
                    <p className="text-3xl font-mono font-semibold text-sky-400">{getElapsedTime()}</p>
                    <p className="text-sm text-slate-500">Entrada: {formatTime(currentEntry?.clock_in)}</p>
                  </div>
                )}

                {status === 'clocked_out' && currentEntry && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500">Horas trabajadas hoy</p>
                    <p className="text-3xl font-mono font-semibold text-emerald-400">{currentEntry.total_hours?.toFixed(2)}h</p>
                    <p className="text-sm text-slate-500">Entrada: {formatTime(currentEntry.clock_in)} - Salida: {formatTime(currentEntry.clock_out)}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-center gap-4 pt-4">
                  {status === 'not_clocked_in' && (
                    <Button
                      onClick={handleClockIn}
                      disabled={actionLoading}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2 px-8 py-6 text-lg"
                      data-testid="clock-in-btn"
                    >
                      <LogIn className="w-5 h-5" />
                      Fichar Entrada
                    </Button>
                  )}
                  {status === 'clocked_in' && (
                    <Button
                      onClick={handleClockOut}
                      disabled={actionLoading}
                      className="bg-rose-600 hover:bg-rose-500 text-white gap-2 px-8 py-6 text-lg"
                      data-testid="clock-out-btn"
                    >
                      <LogOut className="w-5 h-5" />
                      Fichar Salida
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* History */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="font-rajdhani text-xl text-white flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" />
              Historial de Fichajes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No hay registros de fichaje</p>
            ) : (
              <div className="space-y-3">
                {history.slice(0, 10).map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700"
                  >
                    <div>
                      <p className="text-white font-medium capitalize">{formatDate(entry.date)}</p>
                      <p className="text-sm text-slate-400">
                        Entrada: {formatTime(entry.clock_in)} - Salida: {formatTime(entry.clock_out)}
                      </p>
                    </div>
                    <div className="text-right">
                      {entry.total_hours ? (
                        <p className="text-lg font-semibold text-emerald-400">{entry.total_hours.toFixed(2)}h</p>
                      ) : (
                        <p className="text-sm text-amber-400">En curso</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
